import type { Country } from './types';

/**
 * European countries with full metadata.
 */
export const europeCountries: Country[] = [
  {
    code: 'GB',
    name: 'United Kingdom',
    officialName: 'United Kingdom of Great Britain and Northern Ireland',
    flag: '\u{1F1EC}\u{1F1E7}',
    continent: 'Europe',
    subregion: 'British Isles',
    details: {
      capital: 'London',
      population: '67.3M',
      currency: 'GBP (\u00A3)',
      languages: 'English',
      area: '242,495 km\u00B2',
    },
  },
  {
    code: 'FR',
    name: 'France',
    flag: '\u{1F1EB}\u{1F1F7}',
    continent: 'Europe',
    subregion: 'Western Europe',
    details: {
      capital: 'Paris',
      population: '67.8M',
      currency: 'EUR (\u20AC)',
      languages: 'French',
      area: '643,801 km\u00B2',
    },
    mapHints: {
      // France has many overseas territories (French Guiana, Reunion, etc.)
      // Only show mainland France + Corsica
      fitStrategy: 'auto',
      maxTerritoryDistance: 500, // km - excludes overseas departments
    },
  },
  {
    code: 'DE',
    name: 'Germany',
    flag: '\u{1F1E9}\u{1F1EA}',
    continent: 'Europe',
    subregion: 'Central Europe',
    details: {
      capital: 'Berlin',
      population: '83.2M',
      currency: 'EUR (\u20AC)',
      languages: 'German',
      area: '357,022 km\u00B2',
    },
  },
  {
    code: 'IT',
    name: 'Italy',
    flag: '\u{1F1EE}\u{1F1F9}',
    continent: 'Europe',
    subregion: 'Southern Europe',
    details: {
      capital: 'Rome',
      population: '59.1M',
      currency: 'EUR (\u20AC)',
      languages: 'Italian',
      area: '301,340 km\u00B2',
    },
  },
  {
    code: 'ES',
    name: 'Spain',
    flag: '\u{1F1EA}\u{1F1F8}',
    continent: 'Europe',
    subregion: 'Southern Europe',
    details: {
      capital: 'Madrid',
      population: '47.4M',
      currency: 'EUR (\u20AC)',
      languages: 'Spanish',
      area: '505,990 km\u00B2',
    },
    mapHints: {
      // Include Canary Islands (relatively close) but smart algorithm handles this
      fitStrategy: 'auto',
      maxTerritoryDistance: 1500,
    },
  },
  {
    code: 'NL',
    name: 'Netherlands',
    flag: '\u{1F1F3}\u{1F1F1}',
    continent: 'Europe',
    subregion: 'Western Europe',
    details: {
      capital: 'Amsterdam',
      population: '17.5M',
      currency: 'EUR (\u20AC)',
      languages: 'Dutch',
      area: '41,543 km\u00B2',
    },
    mapHints: {
      // Exclude Caribbean Netherlands (Aruba, Curacao, etc.)
      fitStrategy: 'auto',
      maxTerritoryDistance: 500,
    },
  },
  {
    code: 'SE',
    name: 'Sweden',
    flag: '\u{1F1F8}\u{1F1EA}',
    continent: 'Europe',
    subregion: 'Nordic',
    details: {
      capital: 'Stockholm',
      population: '10.4M',
      currency: 'SEK (kr)',
      languages: 'Swedish',
      area: '450,295 km\u00B2',
    },
  },
  {
    code: 'NO',
    name: 'Norway',
    flag: '\u{1F1F3}\u{1F1F4}',
    continent: 'Europe',
    subregion: 'Nordic',
    details: {
      capital: 'Oslo',
      population: '5.4M',
      currency: 'NOK (kr)',
      languages: 'Norwegian',
      area: '385,207 km\u00B2',
    },
    mapHints: {
      // Exclude Svalbard and Jan Mayen (very far north)
      fitStrategy: 'auto',
      maxTerritoryDistance: 800,
    },
  },
  {
    code: 'DK',
    name: 'Denmark',
    flag: '\u{1F1E9}\u{1F1F0}',
    continent: 'Europe',
    subregion: 'Nordic',
    details: {
      capital: 'Copenhagen',
      population: '5.9M',
      currency: 'DKK (kr)',
      languages: 'Danish',
      area: '43,094 km\u00B2',
    },
    mapHints: {
      // Exclude Greenland and Faroe Islands
      fitStrategy: 'auto',
      maxTerritoryDistance: 500,
    },
  },
  {
    code: 'CH',
    name: 'Switzerland',
    flag: '\u{1F1E8}\u{1F1ED}',
    continent: 'Europe',
    subregion: 'Central Europe',
    details: {
      capital: 'Bern',
      population: '8.7M',
      currency: 'CHF (Fr)',
      languages: 'German, French, Italian, Romansh',
      area: '41,285 km\u00B2',
    },
  },
  {
    code: 'AT',
    name: 'Austria',
    flag: '\u{1F1E6}\u{1F1F9}',
    continent: 'Europe',
    subregion: 'Central Europe',
    details: {
      capital: 'Vienna',
      population: '9.0M',
      currency: 'EUR (\u20AC)',
      languages: 'German',
      area: '83,879 km\u00B2',
    },
  },
  {
    code: 'BE',
    name: 'Belgium',
    flag: '\u{1F1E7}\u{1F1EA}',
    continent: 'Europe',
    subregion: 'Western Europe',
    details: {
      capital: 'Brussels',
      population: '11.6M',
      currency: 'EUR (\u20AC)',
      languages: 'Dutch, French, German',
      area: '30,528 km\u00B2',
    },
  },
  {
    code: 'PT',
    name: 'Portugal',
    flag: '\u{1F1F5}\u{1F1F9}',
    continent: 'Europe',
    subregion: 'Southern Europe',
    details: {
      capital: 'Lisbon',
      population: '10.3M',
      currency: 'EUR (\u20AC)',
      languages: 'Portuguese',
      area: '92,212 km\u00B2',
    },
    mapHints: {
      // Include Azores and Madeira (important territories, reasonably close)
      fitStrategy: 'auto',
      maxTerritoryDistance: 2000,
    },
  },
  {
    code: 'GR',
    name: 'Greece',
    flag: '\u{1F1EC}\u{1F1F7}',
    continent: 'Europe',
    subregion: 'Southern Europe',
    details: {
      capital: 'Athens',
      population: '10.7M',
      currency: 'EUR (\u20AC)',
      languages: 'Greek',
      area: '131,957 km\u00B2',
    },
    mapHints: {
      // All Greek islands are relatively close together
      fitStrategy: 'full',
    },
  },
  {
    code: 'PL',
    name: 'Poland',
    flag: '\u{1F1F5}\u{1F1F1}',
    continent: 'Europe',
    subregion: 'Central Europe',
    details: {
      capital: 'Warsaw',
      population: '38.0M',
      currency: 'PLN (z\u0142)',
      languages: 'Polish',
      area: '312,696 km\u00B2',
    },
  },
  {
    code: 'IE',
    name: 'Ireland',
    flag: '\u{1F1EE}\u{1F1EA}',
    continent: 'Europe',
    subregion: 'British Isles',
    details: {
      capital: 'Dublin',
      population: '5.1M',
      currency: 'EUR (\u20AC)',
      languages: 'English, Irish',
      area: '70,273 km\u00B2',
    },
  },
  {
    code: 'IS',
    name: 'Iceland',
    flag: '\u{1F1EE}\u{1F1F8}',
    continent: 'Europe',
    subregion: 'Nordic',
    details: {
      capital: 'Reykjavik',
      population: '372K',
      currency: 'ISK (kr)',
      languages: 'Icelandic',
      area: '103,000 km\u00B2',
    },
  },
  {
    code: 'FI',
    name: 'Finland',
    flag: '\u{1F1EB}\u{1F1EE}',
    continent: 'Europe',
    subregion: 'Nordic',
    details: {
      capital: 'Helsinki',
      population: '5.5M',
      currency: 'EUR (\u20AC)',
      languages: 'Finnish, Swedish',
      area: '338,424 km\u00B2',
    },
  },
  {
    code: 'CZ',
    name: 'Czech Republic',
    officialName: 'Czechia',
    flag: '\u{1F1E8}\u{1F1FF}',
    continent: 'Europe',
    subregion: 'Central Europe',
    details: {
      capital: 'Prague',
      population: '10.7M',
      currency: 'CZK (K\u010D)',
      languages: 'Czech',
      area: '78,866 km\u00B2',
    },
    mapHints: {
      nameInAtlas: 'Czechia',
    },
  },
  {
    code: 'HU',
    name: 'Hungary',
    flag: '\u{1F1ED}\u{1F1FA}',
    continent: 'Europe',
    subregion: 'Central Europe',
    details: {
      capital: 'Budapest',
      population: '9.7M',
      currency: 'HUF (Ft)',
      languages: 'Hungarian',
      area: '93,028 km\u00B2',
    },
  },
  {
    code: 'RO',
    name: 'Romania',
    flag: '\u{1F1F7}\u{1F1F4}',
    continent: 'Europe',
    subregion: 'Eastern Europe',
    details: {
      capital: 'Bucharest',
      population: '19.1M',
      currency: 'RON (lei)',
      languages: 'Romanian',
      area: '238,397 km\u00B2',
    },
  },
  {
    code: 'HR',
    name: 'Croatia',
    flag: '\u{1F1ED}\u{1F1F7}',
    continent: 'Europe',
    subregion: 'Southern Europe',
    details: {
      capital: 'Zagreb',
      population: '4.0M',
      currency: 'EUR (\u20AC)',
      languages: 'Croatian',
      area: '56,594 km\u00B2',
    },
  },
  {
    code: 'SI',
    name: 'Slovenia',
    flag: '\u{1F1F8}\u{1F1EE}',
    continent: 'Europe',
    subregion: 'Central Europe',
    details: {
      capital: 'Ljubljana',
      population: '2.1M',
      currency: 'EUR (\u20AC)',
      languages: 'Slovenian',
      area: '20,273 km\u00B2',
    },
  },
  {
    code: 'SK',
    name: 'Slovakia',
    flag: '\u{1F1F8}\u{1F1F0}',
    continent: 'Europe',
    subregion: 'Central Europe',
    details: {
      capital: 'Bratislava',
      population: '5.4M',
      currency: 'EUR (\u20AC)',
      languages: 'Slovak',
      area: '49,035 km\u00B2',
    },
  },
  {
    code: 'BG',
    name: 'Bulgaria',
    flag: '\u{1F1E7}\u{1F1EC}',
    continent: 'Europe',
    subregion: 'Eastern Europe',
    details: {
      capital: 'Sofia',
      population: '6.9M',
      currency: 'BGN (\u043B\u0432)',
      languages: 'Bulgarian',
      area: '110,879 km\u00B2',
    },
  },
  {
    code: 'EE',
    name: 'Estonia',
    flag: '\u{1F1EA}\u{1F1EA}',
    continent: 'Europe',
    subregion: 'Baltic',
    details: {
      capital: 'Tallinn',
      population: '1.3M',
      currency: 'EUR (\u20AC)',
      languages: 'Estonian',
      area: '45,228 km\u00B2',
    },
  },
  {
    code: 'LV',
    name: 'Latvia',
    flag: '\u{1F1F1}\u{1F1FB}',
    continent: 'Europe',
    subregion: 'Baltic',
    details: {
      capital: 'Riga',
      population: '1.9M',
      currency: 'EUR (\u20AC)',
      languages: 'Latvian',
      area: '64,589 km\u00B2',
    },
  },
  {
    code: 'LT',
    name: 'Lithuania',
    flag: '\u{1F1F1}\u{1F1F9}',
    continent: 'Europe',
    subregion: 'Baltic',
    details: {
      capital: 'Vilnius',
      population: '2.8M',
      currency: 'EUR (\u20AC)',
      languages: 'Lithuanian',
      area: '65,300 km\u00B2',
    },
  },
  {
    code: 'LU',
    name: 'Luxembourg',
    flag: '\u{1F1F1}\u{1F1FA}',
    continent: 'Europe',
    subregion: 'Western Europe',
    details: {
      capital: 'Luxembourg City',
      population: '645K',
      currency: 'EUR (\u20AC)',
      languages: 'Luxembourgish, French, German',
      area: '2,586 km\u00B2',
    },
  },
  {
    code: 'MT',
    name: 'Malta',
    flag: '\u{1F1F2}\u{1F1F9}',
    continent: 'Europe',
    subregion: 'Southern Europe',
    details: {
      capital: 'Valletta',
      population: '520K',
      currency: 'EUR (\u20AC)',
      languages: 'Maltese, English',
      area: '316 km\u00B2',
    },
  },
  {
    code: 'CY',
    name: 'Cyprus',
    flag: '\u{1F1E8}\u{1F1FE}',
    continent: 'Europe',
    subregion: 'Southern Europe',
    details: {
      capital: 'Nicosia',
      population: '1.2M',
      currency: 'EUR (\u20AC)',
      languages: 'Greek, Turkish',
      area: '9,251 km\u00B2',
    },
  },
  {
    code: 'XK',
    name: 'Kosovo',
    flag: '\u{1F1FD}\u{1F1F0}',
    continent: 'Europe',
    subregion: 'Balkans',
    details: {
      capital: 'Pristina',
      population: '1.8M',
      currency: 'EUR (\u20AC)',
      languages: 'Albanian, Serbian',
      area: '10,887 km\u00B2',
    },
  },
  {
    code: 'AL',
    name: 'Albania',
    flag: '\u{1F1E6}\u{1F1F1}',
    continent: 'Europe',
    subregion: 'Southeast Europe',
    details: {
      capital: 'Tirana',
      population: '2.4M',
      currency: 'ALL (L)',
      languages: 'Albanian',
      area: '28,748 km\u00B2',
    },
  },
  {
    code: 'AD',
    name: 'Andorra',
    flag: '\u{1F1E6}\u{1F1E9}',
    continent: 'Europe',
    subregion: 'Southern Europe',
    details: {
      capital: 'Andorra la Vella',
      population: '88K',
      currency: 'EUR (\u20AC)',
      languages: 'Catalan',
      area: '468 km\u00B2',
    },
  },
  {
    code: 'BY',
    name: 'Belarus',
    flag: '\u{1F1E7}\u{1F1FE}',
    continent: 'Europe',
    subregion: 'Eastern Europe',
    details: {
      capital: 'Minsk',
      population: '9.1M',
      currency: 'BYN (Br)',
      languages: 'Belarusian, Russian',
      area: '207,600 km\u00B2',
    },
  },
  {
    code: 'BA',
    name: 'Bosnia and Herzegovina',
    flag: '\u{1F1E7}\u{1F1E6}',
    continent: 'Europe',
    subregion: 'Southeast Europe',
    details: {
      capital: 'Sarajevo',
      population: '3.4M',
      currency: 'BAM (KM)',
      languages: 'Bosnian, Croatian, Serbian',
      area: '51,209 km\u00B2',
    },
    mapHints: {
      nameInAtlas: 'Bosnia and Herz.',
    },
  },
  {
    code: 'LI',
    name: 'Liechtenstein',
    flag: '\u{1F1F1}\u{1F1EE}',
    continent: 'Europe',
    subregion: 'Western Europe',
    details: {
      capital: 'Vaduz',
      population: '41K',
      currency: 'CHF (Fr)',
      languages: 'German',
      area: '160 km\u00B2',
    },
  },
  {
    code: 'MD',
    name: 'Moldova',
    flag: '\u{1F1F2}\u{1F1E9}',
    continent: 'Europe',
    subregion: 'Eastern Europe',
    details: {
      capital: 'Chi\u0219in\u0103u',
      population: '2.7M',
      currency: 'MDL (L)',
      languages: 'Romanian',
      area: '33,847 km\u00B2',
    },
  },
  {
    code: 'MC',
    name: 'Monaco',
    flag: '\u{1F1F2}\u{1F1E8}',
    continent: 'Europe',
    subregion: 'Western Europe',
    details: {
      capital: 'Monaco',
      population: '38K',
      currency: 'EUR (\u20AC)',
      languages: 'French',
      area: '2 km\u00B2',
    },
  },
  {
    code: 'ME',
    name: 'Montenegro',
    flag: '\u{1F1F2}\u{1F1EA}',
    continent: 'Europe',
    subregion: 'Southeast Europe',
    details: {
      capital: 'Podgorica',
      population: '623K',
      currency: 'EUR (\u20AC)',
      languages: 'Montenegrin',
      area: '13,812 km\u00B2',
    },
  },
  {
    code: 'MK',
    name: 'North Macedonia',
    flag: '\u{1F1F2}\u{1F1F0}',
    continent: 'Europe',
    subregion: 'Southeast Europe',
    details: {
      capital: 'Skopje',
      population: '1.8M',
      currency: 'MKD (den)',
      languages: 'Macedonian',
      area: '25,713 km\u00B2',
    },
  },
  {
    code: 'RU',
    name: 'Russia',
    flag: '\u{1F1F7}\u{1F1FA}',
    continent: 'Europe',
    subregion: 'Eastern Europe',
    details: {
      capital: 'Moscow',
      population: '146M',
      currency: 'RUB (\u20BD)',
      languages: 'Russian',
      area: '17,098,246 km\u00B2',
    },
  },
  {
    code: 'SM',
    name: 'San Marino',
    flag: '\u{1F1F8}\u{1F1F2}',
    continent: 'Europe',
    subregion: 'Southern Europe',
    details: {
      capital: 'City of San Marino',
      population: '34K',
      currency: 'EUR (\u20AC)',
      languages: 'Italian',
      area: '61 km\u00B2',
    },
  },
  {
    code: 'RS',
    name: 'Serbia',
    flag: '\u{1F1F7}\u{1F1F8}',
    continent: 'Europe',
    subregion: 'Southeast Europe',
    details: {
      capital: 'Belgrade',
      population: '6.6M',
      currency: 'RSD (\u0434\u0438\u043D.)',
      languages: 'Serbian',
      area: '77,589 km\u00B2',
    },
  },
  {
    code: 'UA',
    name: 'Ukraine',
    flag: '\u{1F1FA}\u{1F1E6}',
    continent: 'Europe',
    subregion: 'Eastern Europe',
    details: {
      capital: 'Kyiv',
      population: '32.9M',
      currency: 'UAH (\u20B4)',
      languages: 'Ukrainian',
      area: '603,550 km\u00B2',
    },
  },
  {
    code: 'VA',
    name: 'Vatican City',
    flag: '\u{1F1FB}\u{1F1E6}',
    continent: 'Europe',
    subregion: 'Southern Europe',
    details: {
      capital: 'Vatican City',
      population: '1K',
      currency: 'EUR (\u20AC)',
      languages: 'Italian, Latin',
      area: '0.44 km\u00B2',
    },
    mapHints: {
      nameInAtlas: 'Vatican',
    },
  },
];
